import React, { createContext, FC, useCallback, useContext, useMemo, useReducer } from "react";
import { billCycle } from "../shared/containers/WellnessPlanPaymentContainer/types";

export interface State {
  pageTitle: string;
  billCycle: billCycle;
  showBackButtonOverride: boolean | null;
}

const initialState: State = {
  pageTitle: "",
  billCycle: "perYear",
  showBackButtonOverride: null,
};

type Action =
  | {
    type: "SET_TITLE"
    title: string
  }
  |
  {
    type: "SET_BILL_CYCLE"
    billCycle: billCycle
  }
  |
  {
    type: "SET_SHOW_BACK_BUTTON_OVERRIDE"
    showBackButtonOverride: boolean | null
  };

export const PageTitleContext = createContext<State | any>(initialState);
PageTitleContext.displayName = "PageTitleContext";

function modalReducer(state: State, action: Action) {
  switch (action.type) {
  case "SET_TITLE": {
    if (state.pageTitle === action.title) return state;
    return {
      ...state,
      pageTitle: action.title,
    };
  }
  case "SET_BILL_CYCLE": {
    if (state.billCycle === action.billCycle) return state;
    return {
      ...state,
      billCycle: action.billCycle,
    };
  }
  case "SET_SHOW_BACK_BUTTON_OVERRIDE": {
    if (state.showBackButtonOverride === action.showBackButtonOverride) return state;
    return {
      ...state,
      showBackButtonOverride: action.showBackButtonOverride,
    };
  }
  }

  return state;
};

export const PageTitleContextProvider: FC = (props) => {
  const [state, dispatch] = useReducer(modalReducer, initialState);
  const setPageTitle = useCallback(
    (pageTitle: string) => dispatch({ type: "SET_TITLE", title: pageTitle }),
    [dispatch],
  );
  const setBillCycle = useCallback(
    (billCycle: billCycle) => dispatch({ type: "SET_BILL_CYCLE", billCycle }),
    [dispatch],
  );
  const setShowBackButtonOverride = useCallback(
    (showBackButtonOverride: boolean | null) =>
      dispatch({ type: "SET_SHOW_BACK_BUTTON_OVERRIDE", showBackButtonOverride }),
    [dispatch],
  );

  const value = useMemo(
    () => ({
      ...state,
      setPageTitle,
      setBillCycle,
      setShowBackButtonOverride,
    }),
    [setBillCycle, setPageTitle, setShowBackButtonOverride, state],
  );

  return <PageTitleContext.Provider value={value} {...props} />;
};

export const usePageTitleContext = () => {
  const context = useContext(PageTitleContext);

  if (context === undefined) {
    throw new Error("usePageTitleContext must be used within a PageTitleContextProvider");
  }

  return context;
};
